import api from './api';
import { nullableUuid } from '@/utils/uuid';

export interface FichaTecnicaItem {
  id?: string;
  produto_id: string;
  quantidade: number | string;
  unidade: string;
  perda_percentual?: number | null;
  sequencia?: number | null;
  substituto_id?: string | null;
}

export interface FichaTecnica {
  id: string;
  produto_id: string;
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  produto_nome?: string | null;
  versao: string;
  rendimento: number;
  perda_padrao_percentual?: number | null;
  status: 'RASCUNHO' | 'APROVADA' | 'OBSOLETA';
  data_aprovacao?: string | null;
  aprovado_por?: string | null;
  itens: FichaTecnicaItem[];
}

export interface FichaTecnicaInput {
  produto_id: string;
  versao: string;
  rendimento: number;
  perda_padrao_percentual?: number | null;
  status?: string;
  itens?: FichaTecnicaItem[];
}

const ENDPOINT = '/industrializacao/ficha-tecnica';

function normalizeFichaPayload<T extends Partial<FichaTecnicaInput>>(data: T): T {
  const payload = {
    ...data,
    itens: data.itens?.map((item) => ({
      ...item,
      produto_id: nullableUuid(item.produto_id) || '',
      substituto_id: nullableUuid(item.substituto_id),
    })),
  };

  if ('produto_id' in data) {
    return {
      ...payload,
      produto_id: nullableUuid(data.produto_id) || '',
    };
  }

  return payload;
}

const fichaTecnicaService = {
  async listar(filters: { produto?: string; status?: string; search?: string } = {}): Promise<FichaTecnica[]> {
    const response = await api.get<FichaTecnica[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<FichaTecnica> {
    const response = await api.get<FichaTecnica>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: FichaTecnicaInput): Promise<FichaTecnica> {
    const response = await api.post<FichaTecnica>(ENDPOINT, normalizeFichaPayload(data));
    return response.data;
  },
  async atualizar(id: string, data: Partial<FichaTecnicaInput>): Promise<FichaTecnica> {
    const response = await api.put<FichaTecnica>(`${ENDPOINT}/${id}`, normalizeFichaPayload(data));
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  async novaVersao(id: string, versao: string): Promise<FichaTecnica> {
    const response = await api.post<FichaTecnica>(`${ENDPOINT}/${id}/nova_versao`, { versao });
    return response.data;
  },
  async aprovar(id: string): Promise<FichaTecnica> {
    const response = await api.post<FichaTecnica>(`${ENDPOINT}/${id}/aprovar`);
    return response.data;
  },
};

export default fichaTecnicaService;
